/**
 * O que faz: Exibe uma página de contatos e abre a conversa escolhida sem baixar todo o período.
 * Onde usa: Detalhes de contatos, anúncios e links do dashboard.
 * Conexões: useContatosDashboard, Dialog compartilhado e DashboardLeadConversation sob demanda.
 * Tabelas: RPC lê campos mínimos de leads; conversa só consulta quando selecionada.
 * Data: 07/10/2026.
 */
import { lazy, Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronLeft, ExternalLink, Info, MessageCircle, Phone } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useContatosDashboard } from '@/hooks/dashboard/useContatosDashboard';
import { useAdAliases } from '@/hooks/useAdAliases';
import type { Lead } from '@/hooks/useSupabaseLeads';
import type { ContextoDashboard, FiltroContatosDashboard, PaginaContatosDashboard } from '@/features/dashboard/data/dashboardContracts';
import { cn } from '@/lib/utils';

// O compositor/histórico do chat não faz parte do bundle necessário para abrir a lista.
const DashboardLeadConversation = lazy(() => import('./DashboardLeadConversation').then(modulo => ({ default: modulo.DashboardLeadConversation })));
type ContatoLista = Pick<Lead, 'id' | 'nome' | 'telefone' | 'created_at' | 'clinica_id' | 'ad_name' | 'origem_lead'> & {
  anuncio?: string | null;
  link_name?: string | null;
};
type Cursor = PaginaContatosDashboard['proximoCursor'];
interface LeadsListModalProps {
  isOpen: boolean; onClose: () => void; leads: Lead[]; title: string; isLoading?: boolean;
  showAdColumn?: boolean; mostrarOrigem?: boolean; adminMode?: boolean; targetClinicaId?: string;
  startDate?: Date | null; endDate?: Date | null; generatedAt?: Date; getAdDisplayName?: (adName: string) => string;
  contexto?: ContextoDashboard; filtro?: FiltroContatosDashboard; totalContatos?: number;
}

// Anúncio ganha do link porque o dashboard já conta o contato uma vez só, no card de anúncios.
// Sem esse texto, a lista de Total de Contatos não diz se a pessoa veio de campanha ou de link.
export const descreverOrigemContato = (
  contato: Pick<ContatoLista, 'ad_name' | 'anuncio' | 'link_name'>,
  apelidoDoAnuncio: (nome: string) => string | null | undefined,
): string => {
  const nomeAnuncio = contato.ad_name?.trim() || contato.anuncio?.trim();
  if (nomeAnuncio) return `Anúncio: ${apelidoDoAnuncio(nomeAnuncio) || nomeAnuncio}`;
  const nomeLink = contato.link_name?.trim();
  if (nomeLink) return `Link personalizado: ${nomeLink}`;
  return 'Sem anúncio nem link personalizado';
};

export const LeadsListModal = ({ isOpen, onClose, leads, title, isLoading = false, showAdColumn = false,
  mostrarOrigem = false, adminMode = false, targetClinicaId, contexto, filtro = { tipo: 'todos' }, totalContatos }: LeadsListModalProps) => {
  const navigate = useNavigate();
  const { getAliasForAd } = useAdAliases(adminMode ? targetClinicaId : undefined);
  const [selecionado, setSelecionado] = useState<ContatoLista | null>(null);
  // A ficha do lead começa recolhida; o estado vive aqui para continuar igual ao trocar de contato.
  const [fichaAberta, setFichaAberta] = useState(false);
  const [tamanho, setTamanho] = useState<20 | 50 | 100>(20);
  const [cursor, setCursor] = useState<Cursor>(null);
  const [anteriores, setAnteriores] = useState<Cursor[]>([]);
  const consulta = useContatosDashboard(contexto, filtro, cursor, tamanho, isOpen);
  const remoto = !!contexto;
  const pagina = anteriores.length + 1;
  const inicio = (pagina - 1) * tamanho;
  const itensRemotos: ContatoLista[] = (consulta.data?.itens ?? []).flatMap(contato => {
    // O schema do RPC valida estes três campos; a guarda mantém o componente seguro
    // mesmo se uma resposta antiga chegar durante uma atualização do frontend.
    if (!contato.id || !contato.clinica_id || !contato.created_at) return [];
    return [{
      id: contato.id,
      clinica_id: contato.clinica_id,
      nome: contato.nome ?? null,
      telefone: contato.telefone ?? null,
      created_at: contato.created_at,
      ad_name: contato.ad_name ?? null,
      anuncio: contato.anuncio ?? null,
      link_name: contato.link_name ?? null,
      origem_lead: contato.origem_lead ?? null,
    }];
  });
  const itensLegados: ContatoLista[] = leads.slice(inicio, inicio + tamanho).map(lead => {
    // A lista antiga pode trazer colunas extras do banco que o tipo Lead ainda não declara.
    const leadComOrigem = lead as Lead & { anuncio?: string | null; link_name?: string | null };
    return {
      id: lead.id,
      clinica_id: lead.clinica_id,
      nome: lead.nome,
      telefone: lead.telefone,
      created_at: lead.created_at,
      ad_name: lead.ad_name,
      origem_lead: lead.origem_lead,
      anuncio: leadComOrigem.anuncio ?? null,
      link_name: leadComOrigem.link_name ?? null,
    };
  });
  const itens: ContatoLista[] = remoto ? itensRemotos : itensLegados;
  const carregando = remoto ? consulta.isLoading : isLoading;
  const total = remoto ? totalContatos : leads.length;
  const proximoCursor: Cursor = consulta.data?.proximoCursor && consulta.data.proximoCursor.created_at && consulta.data.proximoCursor.id
    ? { created_at: consulta.data.proximoCursor.created_at, id: consulta.data.proximoCursor.id }
    : null;
  const podeAvancar = remoto ? !!proximoCursor : inicio + tamanho < leads.length;
  const bloqueado = carregando || (remoto && (consulta.isFetching || !!consulta.error));
  const clinicaDoChat = contexto?.clinicaId ?? (adminMode ? targetClinicaId : selecionado?.clinica_id);

  const proximaPagina = () => {
    // O cursor é o último registro exibido, nunca o excedente usado para detectar a próxima página.
    if (!podeAvancar || bloqueado) return;
    setAnteriores(pilhas => [...pilhas, cursor]);
    setCursor(proximoCursor);
    setSelecionado(null);
  };
  const paginaAnterior = () => {
    if (!anteriores.length || bloqueado) return;
    setCursor(anteriores[anteriores.length - 1]);
    setAnteriores(pilhas => pilhas.slice(0, -1));
    setSelecionado(null);
  };
  const alterarTamanho = (valor: string) => {
    const quantidade = Number(valor);
    // Aceitar somente tamanhos do contrato impede que LIMIT nulo/removido carregue tudo.
    if (quantidade !== 20 && quantidade !== 50 && quantidade !== 100) return;
    setTamanho(quantidade); setCursor(null); setAnteriores([]); setSelecionado(null);
  };
  const abrirChatPrincipal = () => {
    if (!selecionado) return;
    const parametros = new URLSearchParams({ leadId: selecionado.id });
    if (adminMode && clinicaDoChat) parametros.set('adminClinicId', clinicaDoChat);
    onClose(); navigate(`/chat?${parametros.toString()}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={aberto => { if (!aberto) onClose(); }}>
      <DialogContent className="flex h-[94dvh] w-[calc(100vw-1rem)] max-w-6xl flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:w-[94vw]">
        <DialogHeader className="shrink-0 border-b border-border px-4 py-3 pr-12 text-left sm:px-5 sm:pr-12">
          <DialogTitle className="truncate text-base">{title}</DialogTitle>
          <DialogDescription className="text-xs">
            {total === undefined ? 'Contatos atribuídos no período.' : `${total.toLocaleString('pt-BR')} contatos no período.`} Selecione um contato para abrir a conversa.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1">
          {/* Com a ficha aberta a lista só cabe em telas largas; abaixo disso ela recolhe
              para a conversa continuar legível dentro do modal. */}
          <section aria-label="Lista de contatos" aria-busy={carregando} className={cn('min-w-0 flex-1 flex-col', !selecionado ? 'flex' : fichaAberta ? 'hidden border-r border-border xl:flex xl:max-w-[17rem]' : 'hidden border-r border-border sm:flex sm:max-w-sm')}>
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2">
              <p className="text-xs text-muted-foreground">Mais recentes primeiro</p>
              <Select value={String(tamanho)} onValueChange={alterarTamanho}>
                <SelectTrigger className="min-h-11 w-32" aria-label="Contatos por página"><SelectValue /></SelectTrigger>
                <SelectContent>{[20, 50, 100].map(valor => <SelectItem key={valor} value={String(valor)}>{valor} por página</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {remoto && consulta.error ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 p-5 text-center">
                  <p role="alert" className="text-sm text-muted-foreground">Não foi possível carregar os contatos.</p>
                  <Button variant="outline" onClick={() => void consulta.refetch()}>Tentar novamente</Button>
                </div>
              ) : carregando ? <p role="status" className="p-8 text-center text-sm text-muted-foreground">Carregando contatos…</p>
                : !itens.length ? <p role="status" className="p-8 text-center text-sm text-muted-foreground">Nenhum contato encontrado neste recorte.</p>
                  : <ul className="divide-y divide-border">{itens.map(contato => {
                    const nomeAnuncio = contato.ad_name?.trim() || contato.anuncio?.trim();
                    return <li key={contato.id}>
                      <button type="button" disabled={bloqueado} onClick={() => setSelecionado(contato)}
                        aria-label={`Abrir conversa de ${contato.nome || 'contato sem nome'}`} aria-pressed={selecionado?.id === contato.id}
                        className={cn('flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50', selecionado?.id === contato.id && 'bg-accent')}>
                        <div className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-foreground">{contato.nome || 'Nome não informado'}</span>
                          <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Phone className="h-3 w-3" aria-hidden="true" />{contato.telefone || 'Sem telefone'}</span>
                            {contato.created_at && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" aria-hidden="true" />{format(parseISO(contato.created_at), 'dd/MM/yyyy HH:mm')}</span>}
                          </span>
                          {mostrarOrigem ? (
                            <span className="mt-1 block truncate text-xs text-muted-foreground">{descreverOrigemContato(contato, getAliasForAd)}</span>
                          ) : showAdColumn && nomeAnuncio ? (
                            <span className="mt-1 block truncate text-xs text-muted-foreground">{getAliasForAd(nomeAnuncio) || nomeAnuncio}</span>
                          ) : null}
                        </div>
                        <MessageCircle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      </button>
                    </li>;
                  })}</ul>}
            </div>
            <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2">
              <span role="status" className="text-xs tabular-nums text-muted-foreground">Página {pagina} · {itens.length} exibidos{total !== undefined ? ` de ${total}` : ''}</span>
              <div className="flex gap-2">
                <Button variant="outline" className="min-h-11" disabled={pagina === 1 || bloqueado} onClick={paginaAnterior}>Anterior</Button>
                <Button variant="outline" className="min-h-11" disabled={!podeAvancar || bloqueado} onClick={proximaPagina}>Próxima</Button>
              </div>
            </footer>
          </section>
          {selecionado && <section aria-label="Conversa selecionada" className="flex min-w-0 flex-1 flex-col bg-background">
            <header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2">
              <Button variant="ghost" className="min-h-11 min-w-11 p-2" aria-label="Voltar à lista de contatos" onClick={() => setSelecionado(null)}><ChevronLeft className="h-5 w-5" /></Button>
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{selecionado.nome || 'Nome não informado'}</p><p className="truncate text-xs text-muted-foreground">{selecionado.telefone}</p></div>
              {!fichaAberta && (
                <Button variant="ghost" className="min-h-11 min-w-11 gap-2" aria-label="Mostrar informações do lead" aria-expanded={fichaAberta} onClick={() => setFichaAberta(true)}><Info className="h-4 w-4" /><span className="hidden md:inline">Mostrar lead</span></Button>
              )}
              <Button variant="outline" className="min-h-11 min-w-11 gap-2" aria-label="Abrir conversa na aba de chat" onClick={abrirChatPrincipal}><ExternalLink className="h-4 w-4" /><span className="hidden md:inline">Abrir no chat</span></Button>
            </header>
            <div className="min-h-0 flex-1">
              <Suspense fallback={<p role="status" className="p-5 text-sm text-muted-foreground">Abrindo conversa…</p>}>
                <DashboardLeadConversation key={selecionado.id} lead={selecionado} clinicaId={clinicaDoChat} isAdminMode={adminMode} mostrarInfo={fichaAberta} onFecharInfo={() => setFichaAberta(false)} />
              </Suspense>
            </div>
          </section>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
